import { useState, useEffect } from "react";
import { getGalleryPhotos } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Image, 
  Truck, 
  ChefHat, 
  FileText, 
  Search, 
  Filter,
  X,
  Download,
  FolderArchive
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
} from "../components/ui/dialog";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  camion_exterieur: { label: "Camion Extérieur", icon: Truck },
  camion_interieur: { label: "Camion Intérieur", icon: Truck },
  menu: { label: "Menu", icon: FileText },
  plats: { label: "Plats", icon: ChefHat },
};

export default function GalleryPage() {
  const { user } = useAuth();
  const peutExporter = ["admin", "bureau"].includes(user?.role);
  const [photos, setPhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [foodTruckFilter, setFoodTruckFilter] = useState("all");
  const [specialiteFilter, setSpecialiteFilter] = useState("all");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Derived data for filters
  const foodTrucks = [...new Set(photos.map(p => p.food_truck_name))].filter(Boolean).sort();
  const specialites = [...new Set(photos.flatMap(p => p.specialites || []))].filter(Boolean).sort();

  useEffect(() => {
    loadGallery();
  }, []);

  useEffect(() => {
    filterPhotos();
  }, [photos, searchTerm, categoryFilter, foodTruckFilter, specialiteFilter]);

  const loadGallery = async () => {
    try {
      const response = await getGalleryPhotos();
      setPhotos(response.data.photos || []);
    } catch (error) {
      toast.error("Erreur lors du chargement de la galerie");
    } finally {
      setLoading(false);
    }
  };

  // Export all photos to ZIP
  const exportAllPhotos = async () => {
    if (photos.length === 0) {
      toast.error("Aucune photo à exporter");
      return;
    }

    setExporting(true);
    toast.info("Préparation de l'export... Veuillez patienter.");
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/gallery/export-all`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
          timeout: 120000, // 2 minutes timeout for large files
        }
      );

      // Check if response is actually a blob
      if (response.data.size === 0) {
        throw new Error("Fichier vide reçu");
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `album_photos_food_trucks_${new Date().toISOString().split("T")[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Photos exportées avec succès!`);
    } catch (error) {
      console.error("Export error:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Timeout - L'export prend trop de temps. Réessayez.");
      } else {
        toast.error("Erreur lors de l'export des photos");
      }
    } finally {
      setExporting(false);
    }
  };

  // Export filtered photos to ZIP
  const exportFilteredPhotos = async () => {
    if (filteredPhotos.length === 0) {
      toast.error("Aucune photo à exporter");
      return;
    }

    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      
      // Get unique food truck IDs from filtered photos
      const foodTruckIds = [...new Set(filteredPhotos.map(p => p.food_truck_id))];
      
      const response = await axios.post(
        `${API_URL}/api/annuaire/export-photos`,
        { food_truck_ids: foodTruckIds },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `photos_selection_${new Date().toISOString().split("T")[0]}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Photos exportées (${foodTruckIds.length} food truck(s))`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Erreur lors de l'export des photos");
    } finally {
      setExporting(false);
    }
  };

  const filterPhotos = () => {
    let filtered = [...photos];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.food_truck_name?.toLowerCase().includes(term) ||
        (p.specialites || []).some(s => s.toLowerCase().includes(term))
      );
    }
    
    if (categoryFilter !== "all") {
      filtered = filtered.filter(p => p.category === categoryFilter);
    }
    
    if (foodTruckFilter !== "all") {
      filtered = filtered.filter(p => p.food_truck_name === foodTruckFilter);
    }
    
    if (specialiteFilter !== "all") {
      filtered = filtered.filter(p => 
        (p.specialites || []).includes(specialiteFilter)
      );
    }
    
    setFilteredPhotos(filtered);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("all");
    setFoodTruckFilter("all");
    setSpecialiteFilter("all");
  };

  const hasActiveFilters = searchTerm || categoryFilter !== "all" || foodTruckFilter !== "all" || specialiteFilter !== "all";

  const getCategoryInfo = (category) => {
    return CATEGORY_LABELS[category] || { label: category, icon: Image };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary">
            Galerie Photos
          </h1>
          <p className="text-muted-foreground mt-1">
            Découvrez les food trucks du collectif en images
          </p>
        </div>
        
        {/* Export Button */}
        {photos.length > 0 && peutExporter && (
          <Button 
            onClick={hasActiveFilters ? exportFilteredPhotos : exportAllPhotos}
            disabled={exporting}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Export en cours...
              </>
            ) : (
              <>
                <FolderArchive className="w-4 h-4 mr-2" />
                {hasActiveFilters 
                  ? `Exporter la sélection (${new Set(filteredPhotos.map(p => p.food_truck_id)).size} FT)` 
                  : `Exporter tout (${new Set(photos.map(p => p.food_truck_id)).size} FT)`
                }
              </>
            )}
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Image className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">{photos.length}</p>
              <p className="text-xs text-muted-foreground">Photos totales</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <Truck className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">
                {new Set(photos.map(p => p.food_truck_id)).size}
              </p>
              <p className="text-xs text-muted-foreground">Food Trucks</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-status-success/10 flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-status-success" />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">
                {photos.filter(p => p.category === "plats").length}
              </p>
              <p className="text-xs text-muted-foreground">Photos de plats</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-status-warning/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-status-warning" />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">
                {photos.filter(p => p.category === "menu").length}
              </p>
              <p className="text-xs text-muted-foreground">Menus</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E5E0DC]">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un food truck ou une spécialité..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#E5E0DC]"
                data-testid="gallery-search"
              />
            </div>
            
            {/* Filter dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Catégorie */}
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="border-[#E5E0DC]" data-testid="gallery-category-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les catégories</SelectItem>
                  <SelectItem value="camion_exterieur">Camion Extérieur</SelectItem>
                  <SelectItem value="camion_interieur">Camion Intérieur</SelectItem>
                  <SelectItem value="menu">Menu</SelectItem>
                  <SelectItem value="plats">Plats</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Food Truck */}
              <Select value={foodTruckFilter} onValueChange={setFoodTruckFilter}>
                <SelectTrigger className="border-[#E5E0DC]" data-testid="gallery-foodtruck-filter">
                  <Truck className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Food Truck" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les food trucks</SelectItem>
                  {foodTrucks.map(ft => (
                    <SelectItem key={ft} value={ft}>{ft}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Spécialité */}
              <Select value={specialiteFilter} onValueChange={setSpecialiteFilter}>
                <SelectTrigger className="border-[#E5E0DC]" data-testid="gallery-specialite-filter">
                  <ChefHat className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Spécialité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les spécialités</SelectItem>
                  {specialites.map(spec => (
                    <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Clear filters button */}
              {hasActiveFilters && (
                <Button 
                  variant="outline" 
                  onClick={clearFilters}
                  className="border-[#E5E0DC]"
                >
                  <X className="w-4 h-4 mr-2" />
                  Effacer les filtres
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gallery Grid */}
      {filteredPhotos.length === 0 ? (
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="py-16 text-center">
            <Image className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-lg">Aucune photo dans la galerie</p>
            <p className="text-sm text-muted-foreground mt-1">
              Les food trucks peuvent ajouter leurs photos depuis leur espace membre
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredPhotos.map((photo, index) => {
            const CategoryIcon = getCategoryInfo(photo.category).icon;
            return (
              <Card 
                key={`${photo.file_id}-${index}`}
                className="bg-white border-[#E5E0DC] overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setSelectedPhoto(photo)}
                data-testid={`gallery-photo-${index}`}
              >
                <div className="relative aspect-square">
                  <img
                    src={`${API_URL}/api/files/${photo.file_id}`}
                    alt={`${photo.food_truck_name} - ${getCategoryInfo(photo.category).label}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23F0EBE6' width='200' height='200'/%3E%3Ctext fill='%238B4513' x='100' y='100' text-anchor='middle' dy='.3em' font-size='14'%3EImage non disponible%3C/text%3E%3C/svg%3E";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform">
                    <p className="text-white font-semibold text-sm truncate">{photo.food_truck_name}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <CategoryIcon className="w-3 h-3 text-white/80" />
                      <span className="text-white/80 text-xs">{getCategoryInfo(photo.category).label}</span>
                    </div>
                  </div>
                </div>
                <CardContent className="p-3">
                  <p className="font-medium text-secondary text-sm truncate">{photo.food_truck_name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs border-[#E5E0DC]">
                      <CategoryIcon className="w-3 h-3 mr-1" />
                      {getCategoryInfo(photo.category).label}
                    </Badge>
                  </div>
                  {photo.specialites && photo.specialites.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2 truncate">
                      {photo.specialites.slice(0, 2).join(", ")}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Photo Modal */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {selectedPhoto && (
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full"
                onClick={() => setSelectedPhoto(null)}
              >
                <X className="w-5 h-5" />
              </Button>
              <img
                src={`${API_URL}/api/files/${selectedPhoto.file_id}`}
                alt={`${selectedPhoto.food_truck_name} - ${getCategoryInfo(selectedPhoto.category).label}`}
                className="w-full max-h-[70vh] object-contain bg-black"
              />
              <div className="p-4 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-heading font-bold text-secondary">
                      {selectedPhoto.food_truck_name}
                    </h3>
                    <Badge variant="outline" className="mt-1">
                      {getCategoryInfo(selectedPhoto.category).label}
                    </Badge>
                  </div>
                  {selectedPhoto.specialites && selectedPhoto.specialites.length > 0 && (
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Spécialités</p>
                      <p className="text-sm font-medium text-secondary">
                        {selectedPhoto.specialites.join(", ")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
